import { useCallback, useState, useEffect } from 'react';
import { usePaginationQuery } from './usePaginationQuery';
import { PaginationConfig, PaginationEvent } from '../types';

interface PageCursor {
  pageNumber: number;
  cursor: string | null;
}

export interface UsePaginationOptions {
  initialCursor?: string | null;
  pageSize: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  nextCursor?: string | null;
  total?: number;
  onPageChange?: (event: PaginationEvent) => void;
  onPageSizeChange?: (event: PaginationEvent) => void;
  queryKey?: string;
  syncWithUrl?: boolean;
  /** 탭 컨텍스트의 params (탭별 독립적인 상태를 위해 사용) */
  tabParams?: Record<string, unknown>;
  /** 탭 params 업데이트 함수 */
  onTabParamsUpdate?: (params: Record<string, unknown>) => void;
}

export interface UsePaginationReturn {
  cursor: string | null;
  currentPageIndex: number;
  pageSize: number;
  total: number | undefined;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  goToPrevious: () => void;
  goToNext: () => void;
  setPageSize: (pageSize: number) => void;
  setCursorAndIndex: (cursor: string | null, index: number) => void;
  isFirstPage: boolean;
  isLastPage: boolean;
  config: PaginationConfig;
  pageCursors: PageCursor[];
}

export function usePagination({
  initialCursor = null,
  pageSize,
  hasPreviousPage,
  hasNextPage,
  nextCursor,
  total,
  onPageChange,
  onPageSizeChange,
  queryKey = 'page',
  syncWithUrl = true,
  tabParams,
  onTabParamsUpdate,
}: UsePaginationOptions): UsePaginationReturn {
  const [pageCursors, setPageCursors] = useState<PageCursor[]>([{ pageNumber: 1, cursor: null }]);

  const { cursor, pageIndex, setCursorAndIndex } = usePaginationQuery({
    queryKey,
    initialCursor,
    initialPageIndex: 1,
    syncWithUrl,
    tabParams,
    onTabParamsUpdate,
  });

  useEffect(() => {
    setPageCursors((prev) => {
      const existing = prev.find((p) => p.pageNumber === pageIndex);
      if (existing && existing.cursor === cursor) {
        return prev; // 변경 없음
      }
      const filtered = prev.filter((p) => p.pageNumber !== pageIndex);
      return [...filtered, { pageNumber: pageIndex, cursor }];
    });
  }, [pageIndex, cursor]);

  const goToPrevious = useCallback(() => {
    if (pageIndex <= 1) return;

    const previousIndex = pageIndex - 1;
    const previousPageInfo = pageCursors.find((p) => p.pageNumber === previousIndex);

    if (!previousPageInfo) {
      setPageCursors([{ pageNumber: 1, cursor: null }]);
      setCursorAndIndex(null, 1);
      onPageChange?.({ cursor: null });
      return;
    }

    setCursorAndIndex(previousPageInfo.cursor, previousIndex);
    onPageChange?.({ cursor: previousPageInfo.cursor });
  }, [pageIndex, pageCursors, setCursorAndIndex, onPageChange]);

  const goToNext = useCallback(() => {
    if (!hasNextPage || !nextCursor) return;

    const newIndex = pageIndex + 1;

    // 다음 페이지로 이동하기 전에 현재 페이지 커서를 저장 (이전 페이지로 돌아올 때 사용)
    // 이미 같은 값이면 업데이트하지 않음 (불필요한 리렌더링 방지)
    setPageCursors((prev) => {
      const existing = prev.find((p) => p.pageNumber === pageIndex);
      if (existing && existing.cursor === cursor) {
        return prev; // 변경 없음
      }
      // 기존 항목 업데이트 또는 새로 추가
      const filtered = prev.filter((p) => p.pageNumber !== pageIndex);
      return [...filtered, { pageNumber: pageIndex, cursor }];
    });

    setCursorAndIndex(nextCursor, newIndex);
    onPageChange?.({ cursor: nextCursor });
  }, [hasNextPage, nextCursor, pageIndex, cursor, setCursorAndIndex, onPageChange]);

  // 페이지 크기 변경
  const setPageSize = useCallback(
    (newPageSize: number) => {
      if (newPageSize > 0) {
        // 배열 초기화
        setPageCursors([{ pageNumber: 1, cursor: null }]);
        // 첫 페이지로 이동
        setCursorAndIndex(null, 1);

        const event: PaginationEvent = {
          cursor: null,
          pageSize: newPageSize,
        };
        onPageSizeChange?.(event);
        onPageChange?.(event);
      }
    },
    [setCursorAndIndex, onPageChange, onPageSizeChange]
  );

  // hasPreviousPage는 pageIndex > 1이면 true
  // 외부에서 전달받은 hasPreviousPage와 pageIndex를 모두 고려
  const actualHasPreviousPage = pageIndex > 1 || hasPreviousPage;
  const isFirstPage = pageIndex === 1;
  const isLastPage = !hasNextPage;

  const config: PaginationConfig = {
    cursor,
    hasPreviousPage: actualHasPreviousPage,
    hasNextPage,
    pageSize,
    total,
  };

  return {
    cursor,
    currentPageIndex: pageIndex,
    pageSize,
    total,
    hasPreviousPage: actualHasPreviousPage,
    hasNextPage,
    goToPrevious,
    goToNext,
    setPageSize,
    setCursorAndIndex,
    isFirstPage,
    isLastPage,
    config,
    pageCursors,
  };
}
